(function () {
  'use strict';

  const config = window.MorldoroCartConfig || {};
  const TRANSITION_MS = 320;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canAutoFocus = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  const state = {
    isOpen: false,
    editorPreviewOpen: false,
    selectedSectionId: null,
    lastFocus: null,
    scrollY: 0,
    scrollLocked: false,
    pendingRequests: 0,
    sectionRenderInFlight: false,
    priceSyncInFlight: false,
    closeTimer: null,
  };

  let abortController = null;

  const dom = {
    get drawer() {
      return document.querySelector('[data-cart-drawer]');
    },
    get panel() {
      return document.querySelector('[data-cart-panel]');
    },
    get overlay() {
      return document.querySelector('[data-cart-overlay]');
    },
    get contentRoot() {
      return document.querySelector('[data-cart-drawer-content]');
    },
    get errorNode() {
      return document.querySelector('[data-cart-drawer-error]');
    },
    get sectionEl() {
      return document.querySelector('.custom-cart-drawer-section');
    },
  };

  function getRoot() {
    const root = window.Shopify?.routes?.root || config.routes?.root || '/';
    if (root === '/') return '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function getSectionKey() {
    if (dom.sectionEl) {
      return dom.sectionEl.id.replace('shopify-section-', '');
    }
    return config.sectionId || null;
  }

  function getSectionRoot(sectionId) {
    if (!sectionId) return null;
    return document.getElementById(`shopify-section-${sectionId}`);
  }

  function isCartSection(sectionId) {
    const root = getSectionRoot(sectionId);
    return Boolean(root && root.classList.contains('custom-cart-drawer-section'));
  }

  function syncConfigFromSection() {
    if (dom.sectionEl) {
      config.sectionId = dom.sectionEl.id.replace('shopify-section-', '');
    }

    const content = dom.contentRoot;
    if (content) {
      const sectionId = content.getAttribute('data-cart-section-id');
      if (sectionId) {
        config.sectionId = sectionId;
      }
    }
  }

  function formatMoney(cents) {
    const amount = typeof cents === 'number' ? cents : parseInt(String(cents), 10);
    const format = config.moneyFormat || '{{amount}}';

    let formatted;
    if (window.Shopify?.formatMoney) {
      formatted = window.Shopify.formatMoney(amount, format);
    } else {
      const value = (amount / 100).toFixed(2);
      formatted = format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amount / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, value);
    }

    if (amount % 100 === 0) {
      formatted = formatted.replace(/([.,]00)(?!\d)/g, '');
    }

    return formatted;
  }

  const ScrollLock = {
    getScrollbarWidth() {
      return window.innerWidth - document.documentElement.clientWidth;
    },

    lock() {
      if (state.scrollLocked) return;

      state.scrollY = window.scrollY || window.pageYOffset || 0;
      const scrollbarWidth = this.getScrollbarWidth();

      document.documentElement.classList.add('custom-cart-open');
      document.body.classList.add('custom-cart-open');
      document.body.style.top = `-${state.scrollY}px`;
      document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';

      state.scrollLocked = true;
    },

    unlock() {
      if (!state.scrollLocked) return;

      document.documentElement.classList.remove('custom-cart-open');
      document.body.classList.remove('custom-cart-open');
      document.body.style.top = '';
      document.body.style.paddingRight = '';

      window.scrollTo(0, state.scrollY);
      state.scrollLocked = false;
    },
  };

  function layoutMobileCartBadge(node, label) {
    const svg = node.closest('.mobile-cart-button__badge-svg');
    if (!svg) return;

    const shape = svg.querySelector('[data-cart-badge-shape]');
    if (!shape) return;

    const baseHeight = 11.503;
    const width = label.length > 1 ? baseHeight + (label.length - 1) * 3.6 : baseHeight;

    svg.setAttribute('viewBox', '0 0 ' + width + ' ' + baseHeight);
    shape.setAttribute('width', String(width));
    node.setAttribute('x', '50%');
    node.setAttribute('y', '6.05');
  }

  function updateBadge(itemCount) {
    const countLabel = itemCount > 99 ? '99+' : String(itemCount);
    const badges = document.querySelectorAll('[data-hero-cart-badge]');
    const countNodes = document.querySelectorAll('[data-hero-cart-count]');

    countNodes.forEach(function (node) {
      node.textContent = countLabel;
      layoutMobileCartBadge(node, countLabel);
    });

    badges.forEach(function (badge) {
      if (itemCount > 0) {
        badge.hidden = false;
        badge.classList.remove('hero-desktop__cart-badge--hidden', 'mobile-cart-badge--hidden');
      } else {
        badge.hidden = true;
        badge.classList.add('hero-desktop__cart-badge--hidden', 'mobile-cart-badge--hidden');
      }
    });
  }

  function dispatchCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }

  function showError(message) {
    if (!dom.errorNode) return;
    dom.errorNode.textContent = message || config.strings?.errorGeneric || '';
    dom.errorNode.hidden = !message;
  }

  async function fetchCart() {
    const response = await fetch(`${getRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed (${response.status})`);
    }

    return response.json();
  }

  async function fetchVariantPrice(variantId) {
    try {
      const response = await fetch(`${getRoot()}variants/${variantId}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) return null;

      const variant = await response.json();
      return typeof variant.price === 'number' ? variant.price : null;
    } catch (error) {
      console.warn('[MorldoroCart] Variant price fetch failed:', variantId, error);
      return null;
    }
  }

  async function syncCartPrices(cart) {
    if (!cart?.items?.length || state.priceSyncInFlight) return cart;

    const staleLines = [];

    for (const item of cart.items) {
      const livePrice = await fetchVariantPrice(item.variant_id);
      if (livePrice != null && livePrice !== item.original_price) {
        staleLines.push(item);
      }
    }

    if (staleLines.length === 0) return cart;

    state.priceSyncInFlight = true;

    try {
      for (const item of staleLines) {
        const removeResponse = await fetch(`${getRoot()}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });

        if (!removeResponse.ok) {
          console.warn('[MorldoroCart] Price sync remove failed for line:', item.key);
        }
      }

      const itemsToAdd = staleLines.map(function (item) {
        return {
          id: Number(item.variant_id),
          quantity: item.quantity,
        };
      });

      const addResponse = await fetch(`${getRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items: itemsToAdd }),
      });

      if (!addResponse.ok) {
        console.warn('[MorldoroCart] Price sync re-add failed:', await addResponse.text());
        return cart;
      }

      return fetchCart();
    } catch (error) {
      console.warn('[MorldoroCart] Price sync failed:', error);
      return cart;
    } finally {
      state.priceSyncInFlight = false;
    }
  }

  async function renderDrawerFromSection() {
    const sectionKey = getSectionKey();
    if (!sectionKey || state.sectionRenderInFlight) return false;

    state.sectionRenderInFlight = true;

    try {
      const response = await fetch(`${getRoot()}?sections=${encodeURIComponent(sectionKey)}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        console.error('[MorldoroCart] Section render failed:', response.status);
        return false;
      }

      const payload = await response.json();
      const html = payload[sectionKey];
      if (!html) return false;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nextContent = doc.querySelector('[data-cart-drawer-content]');
      const currentContent = dom.contentRoot;

      if (nextContent && currentContent) {
        currentContent.replaceWith(document.importNode(nextContent, true));
        syncConfigFromSection();
        syncDialogAria();
        return true;
      }

      return false;
    } catch (error) {
      console.error('[MorldoroCart] Section render error:', error);
      return false;
    } finally {
      state.sectionRenderInFlight = false;
    }
  }

  async function refreshCartUI(cart) {
    let currentCart = cart || (await fetchCart());
    currentCart = await syncCartPrices(currentCart);

    updateBadge(currentCart.item_count);
    const rendered = await renderDrawerFromSection();
    if (!rendered) {
      console.warn('[MorldoroCart] Section render failed; drawer content may be stale.');
    }
    dispatchCartUpdated(currentCart);
    document.dispatchEvent(new CustomEvent('cart:ui-refreshed'));

    if (isDesignMode && state.selectedSectionId && state.editorPreviewOpen) {
      window.requestAnimationFrame(function () {
        openDrawer(null, { preview: true });
      });
    }

    return currentCart;
  }

  /**
   * Clears the Shopify cart after a real order is created.
   * Not called during dry-run or on checkout errors.
   */
  async function clearCartAfterOrder() {
    const response = await fetch(`${getRoot()}cart/clear.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Cart clear failed (${response.status})`);
    }

    const cart = await response.json();
    await refreshCartUI(cart);
    return cart;
  }

  function focusDrawer() {
    if (!canAutoFocus) return;

    const drawer = dom.drawer;
    if (!drawer) return;

    const closeBtn = drawer.querySelector('[data-cart-drawer-close]');
    if (closeBtn instanceof HTMLElement) {
      closeBtn.focus({ preventScroll: true });
      return;
    }

    const panel = dom.panel;
    if (panel instanceof HTMLElement) {
      panel.focus({ preventScroll: true });
    }
  }

  function syncDialogAria() {
    const panel = dom.panel;
    if (!(panel instanceof HTMLElement)) return;

    const title = document.getElementById('custom-cart-drawer-title');
    if (title) {
      panel.setAttribute('aria-labelledby', 'custom-cart-drawer-title');
      panel.removeAttribute('aria-label');
      return;
    }

    panel.removeAttribute('aria-labelledby');
    panel.setAttribute('aria-label', config.strings?.dialogLabel || 'Cart');
  }

  function openDrawer(trigger, options) {
    const drawer = dom.drawer;
    const panel = dom.panel;
    const preview = options?.preview === true;

    if (preview && options?.sectionId) {
      state.selectedSectionId = options.sectionId;
    }

    if (!drawer || !panel) {
      ScrollLock.unlock();
      return false;
    }

    if (state.isOpen && !preview) {
      return true;
    }

    try {
      if (trigger instanceof HTMLElement) {
        state.lastFocus = trigger;
      } else if (!preview) {
        state.lastFocus = document.activeElement;
      }

      if (state.closeTimer) {
        window.clearTimeout(state.closeTimer);
        state.closeTimer = null;
      }

      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      panel.setAttribute('aria-hidden', 'false');

      window.requestAnimationFrame(function () {
        drawer.classList.add('is-open');

        window.requestAnimationFrame(function () {
          ScrollLock.lock();
          syncDialogAria();
          focusDrawer();
        });
      });

      state.isOpen = true;
      state.editorPreviewOpen = preview;
      return true;
    } catch (error) {
      console.error('[MorldoroCart] Open drawer failed:', error);
      ScrollLock.unlock();
      state.isOpen = false;
      state.editorPreviewOpen = false;
      return false;
    }
  }

  function closeDrawer(options) {
    const force = options?.force === true;
    const drawer = dom.drawer;
    const panel = dom.panel;

    if (!drawer) {
      ScrollLock.unlock();
      return;
    }

    if (isDesignMode && state.editorPreviewOpen && !force && state.selectedSectionId) {
      return;
    }

    if (!state.isOpen) {
      ScrollLock.unlock();
      return;
    }

    try {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      panel?.setAttribute('aria-hidden', 'true');
      state.isOpen = false;
      state.editorPreviewOpen = false;
      ScrollLock.unlock();
      document.dispatchEvent(new CustomEvent('cart:drawer-closed'));

      const delay = prefersReducedMotion ? 0 : TRANSITION_MS;
      state.closeTimer = window.setTimeout(function () {
        if (!state.isOpen) {
          drawer.hidden = true;
        }

        if (state.lastFocus instanceof HTMLElement && !isDesignMode) {
          state.lastFocus.focus({ preventScroll: true });
        }

        state.closeTimer = null;
      }, delay);
    } catch (error) {
      console.error('[MorldoroCart] Close drawer failed:', error);
      ScrollLock.unlock();
      state.isOpen = false;
      state.editorPreviewOpen = false;
    }
  }

  function trapFocus(event) {
    const panel = dom.panel;
    if (!state.isOpen || !panel || event.key !== 'Tab') return;

    const focusable = panel.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function getAddRoot(trigger) {
    return trigger.closest('[data-cart-add-root]') || trigger.closest('[data-second-desktop-purchase]');
  }

  function resolveQuantity(trigger) {
    const root = getAddRoot(trigger);
    const output = root?.querySelector('[data-quantity-value]');
    if (output) {
      const parsed = parseInt(output.textContent || '1', 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    const attrQty = trigger.getAttribute('data-quantity');
    if (attrQty) {
      const parsed = parseInt(attrQty, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    return 1;
  }

  function hasQuantitySelector(trigger) {
    const root = getAddRoot(trigger);
    return Boolean(root?.querySelector('[data-quantity-value]'));
  }

  function findCartLineByVariantId(cart, variantId) {
    return (
      cart.items.find(function (item) {
        return String(item.variant_id) === String(variantId);
      }) || null
    );
  }

  function resolveVariantId(trigger) {
    const fromSelf = trigger.getAttribute('data-variant-id');
    if (fromSelf) return fromSelf;

    const root = trigger.closest('[data-variant-id]');
    return root?.getAttribute('data-variant-id') || root?.dataset?.variantId || null;
  }

  function setTriggerLoading(trigger, isLoading) {
    if (!(trigger instanceof HTMLElement)) return;

    const ctaText = trigger.querySelector('[data-purchase-cta-text]');
    const purchaseRoot = trigger.closest('[data-purchase-mobile]');
    const loadingLabel = purchaseRoot?.getAttribute('data-loading-cta-label');
    const defaultLabel = purchaseRoot?.getAttribute('data-default-cta-label');

    if (ctaText && loadingLabel) {
      if (isLoading) {
        if (!ctaText.dataset.defaultLabel) {
          ctaText.dataset.defaultLabel = ctaText.textContent || defaultLabel || '';
        }
        ctaText.textContent = loadingLabel;
      } else if (ctaText.dataset.defaultLabel) {
        ctaText.textContent = ctaText.dataset.defaultLabel;
      }
    }

    if (isLoading) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.disabled = true;
      trigger.classList.add('is-cart-loading');
      return;
    }

    trigger.removeAttribute('aria-busy');
    trigger.classList.remove('is-cart-loading');
    if (resolveVariantId(trigger)) {
      trigger.disabled = false;
    }
  }

  async function upsertCartQuantity(variantId, quantity, options) {
    const openAfter = options?.openDrawer !== false;
    const trigger = options?.trigger || null;
    const explicitQuantity = options?.explicitQuantity === true;

    if (state.pendingRequests > 0) return null;

    state.pendingRequests += 1;
    showError('');
    setTriggerLoading(trigger, true);

    try {
      let cart = await fetchCart();
      const existingLine = findCartLineByVariantId(cart, variantId);

      if (!explicitQuantity && quantity === 1) {
        if (existingLine) {
          await refreshCartUI(cart);
          if (openAfter) {
            openDrawer(trigger, { preview: false });
          }
          return cart;
        }
      } else if (existingLine) {
        if (existingLine.quantity === quantity) {
          await refreshCartUI(cart);
          if (openAfter) {
            openDrawer(trigger, { preview: false });
          }
          return cart;
        }

        const changeResponse = await fetch(`${getRoot()}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ id: existingLine.key, quantity: quantity }),
        });

        const changeBody = await changeResponse.json().catch(function () {
          return null;
        });

        if (!changeResponse.ok) {
          const message = changeBody?.description || changeBody?.message || config.strings?.errorUnavailable;
          console.error('[MorldoroCart] Cart change failed:', message, changeBody);
          throw new Error(message);
        }

        await refreshCartUI(changeBody);
        if (openAfter) {
          openDrawer(trigger, { preview: false });
        }
        return changeBody;
      }

      const response = await fetch(`${getRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity }] }),
      });

      const body = await response.json().catch(function () {
        return null;
      });

      if (!response.ok) {
        const message = body?.description || body?.message || config.strings?.errorUnavailable;
        console.error('[MorldoroCart] Add to cart failed:', message, body);
        throw new Error(message);
      }

      cart = await fetchCart();
      await refreshCartUI(cart);

      if (openAfter) {
        openDrawer(trigger, { preview: false });
      }

      return cart;
    } catch (error) {
      showError(error.message || config.strings?.errorGeneric);
      openDrawer(trigger, { preview: false });
      throw error;
    } finally {
      state.pendingRequests -= 1;
      setTriggerLoading(trigger, false);
    }
  }

  async function addItems(items, options) {
    const openAfter = options?.openDrawer !== false;
    const trigger = options?.trigger || null;

    if (state.pendingRequests > 0) return null;

    state.pendingRequests += 1;
    showError('');
    setTriggerLoading(trigger, true);

    try {
      const response = await fetch(`${getRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items }),
      });

      const body = await response.json().catch(function () {
        return null;
      });

      if (!response.ok) {
        const message = body?.description || body?.message || config.strings?.errorUnavailable;
        console.error('[MorldoroCart] Add to cart failed:', message, body);
        throw new Error(message);
      }

      const cart = await fetchCart();
      await refreshCartUI(cart);

      if (openAfter) {
        openDrawer(trigger, { preview: false });
      }

      return cart;
    } catch (error) {
      showError(error.message || config.strings?.errorGeneric);
      openDrawer(trigger, { preview: false });
      throw error;
    } finally {
      state.pendingRequests -= 1;
      setTriggerLoading(trigger, false);
    }
  }

  async function changeLine(lineKey, quantity) {
    if (state.pendingRequests > 0) return null;

    state.pendingRequests += 1;
    showError('');

    const line = document.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`);
    const qtyWrap = line?.querySelector('[data-cart-qty]');
    qtyWrap?.classList.add('is-loading');

    try {
      const response = await fetch(`${getRoot()}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity }),
      });

      const body = await response.json().catch(function () {
        return null;
      });

      if (!response.ok) {
        const message = body?.description || body?.message || config.strings?.errorUnavailable;
        console.error('[MorldoroCart] Cart change failed:', message, body);
        throw new Error(message);
      }

      await refreshCartUI(body);
      return body;
    } catch (error) {
      showError(error.message || config.strings?.errorGeneric);
      throw error;
    } finally {
      state.pendingRequests -= 1;
      qtyWrap?.classList.remove('is-loading');
    }
  }

  function onDocumentClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-cart-drawer-preview]')) {
      return;
    }

    const openTrigger = target.closest('[data-cart-open]');
    if (openTrigger) {
      event.preventDefault();
      fetchCart()
        .then(refreshCartUI)
        .then(function () {
          openDrawer(openTrigger, { preview: false });
        })
        .catch(function (error) {
          console.warn('[MorldoroCart] Cart refresh on open failed:', error);
          openDrawer(openTrigger, { preview: false });
        });
      return;
    }

    if (target.closest('[data-cart-overlay]') && state.isOpen) {
      if (window.MorldoroCheckout?.isDismissLocked?.()) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      closeDrawer({ force: true });
      return;
    }

    const closeTrigger = target.closest('[data-cart-drawer-close]');
    if (closeTrigger && state.isOpen) {
      event.preventDefault();
      closeDrawer({ force: true });
      return;
    }

    const addTrigger = target.closest('[data-cart-add]');
    if (addTrigger) {
      event.preventDefault();
      const variantId = resolveVariantId(addTrigger);
      if (!variantId) {
        showError(config.strings?.errorUnavailable || 'Product is not configured.');
        openDrawer(addTrigger, { preview: false });
        return;
      }

      const quantity = resolveQuantity(addTrigger);
      const explicitQuantity = hasQuantitySelector(addTrigger);
      const inEmptyDrawer = Boolean(addTrigger.closest('[data-cart-drawer-empty]'));
      upsertCartQuantity(variantId, quantity, {
        trigger: addTrigger,
        openDrawer: !inEmptyDrawer,
        explicitQuantity: explicitQuantity,
      }).catch(function () {});
      return;
    }

    const qtyBtn = target.closest('[data-cart-qty-change]');
    if (qtyBtn) {
      event.preventDefault();
      const line = qtyBtn.closest('[data-cart-line]');
      const lineKey = line?.getAttribute('data-line-key');
      const valueNode = line?.querySelector('[data-cart-qty-value]');
      if (!lineKey || !valueNode) return;

      const delta = parseInt(qtyBtn.getAttribute('data-cart-qty-change') || '0', 10);
      const current = parseInt(valueNode.textContent || '1', 10);
      const next = Math.max(0, current + delta);
      if (next === current) return;

      changeLine(lineKey, next).catch(function () {});
      return;
    }

    const removeBtn = target.closest('[data-cart-remove]');
    if (removeBtn) {
      event.preventDefault();
      const lineKey = removeBtn.closest('[data-cart-line]')?.getAttribute('data-line-key');
      if (!lineKey) return;
      changeLine(lineKey, 0).catch(function () {});
    }
  }

  function onDocumentKeydown(event) {
    if (event.key === 'Escape' && state.isOpen) {
      if (window.MorldoroCheckout?.handleEscape?.()) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      closeDrawer({ force: true });
      return;
    }
    trapFocus(event);
  }

  function onSectionSelect(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isCartSection(sectionId)) return;

    state.selectedSectionId = sectionId;
    openDrawer(null, { preview: true, sectionId: sectionId });
  }

  function onSectionDeselect(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isCartSection(sectionId)) return;

    state.selectedSectionId = null;
    closeDrawer({ force: true });
  }

  function onSectionLoad(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isCartSection(sectionId)) return;

    syncConfigFromSection();

    if (state.selectedSectionId === sectionId) {
      window.requestAnimationFrame(function () {
        openDrawer(null, { preview: true });
      });
      return;
    }

    fetchCart()
      .then(refreshCartUI)
      .catch(function () {});
  }

  function onSectionUnload(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId || !isCartSection(sectionId)) return;

    if (state.isOpen) {
      closeDrawer({ force: true });
    }
  }

  function destroyBindings() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  function bindEvents() {
    destroyBindings();
    abortController = new AbortController();
    const signal = abortController.signal;

    document.addEventListener('click', onDocumentClick, { signal });
    document.addEventListener('keydown', onDocumentKeydown, { signal });

    if (isDesignMode) {
      document.addEventListener('shopify:section:select', onSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', onSectionDeselect, { signal });
      document.addEventListener('shopify:section:load', onSectionLoad, { signal });
      document.addEventListener('shopify:section:unload', onSectionUnload, { signal });
    }
  }

  function init() {
    syncConfigFromSection();
    bindEvents();

    fetchCart()
      .then(function (cart) {
        updateBadge(cart.item_count);
      })
      .catch(function (error) {
        console.warn('[MorldoroCart] Initial cart fetch failed:', error);
      });
  }

  window.MorldoroCart = {
    addItems,
    changeLine,
    fetchCart,
    openDrawer,
    closeDrawer,
    refreshCartUI,
    clearCartAfterOrder,
    formatMoney,
    upsertCartQuantity,
    showError,
    reinit: init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
